import { useState, useEffect, useRef } from "react";
import InsertFieldButton from "./InsertFieldButton";
import "../../../styles/components/management/Chat/SuggestedReply.css";

export default function SuggestedReply({ bookingId, onInject, booking = {} }) {
  const [isOpen, setIsOpen] = useState(false);
  const [chatLog, setChatLog] = useState([]);
  const [currentHint, setCurrentHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleInteraction = (e) => {
      if (
        !e.target.closest(".booking-fields-dropdown") &&
        !e.target.closest(".insert-field-button")
      ) {
        setShowFields(false);
      }
      if (e.key === "Escape") {
        setShowFields(false);
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleInteraction);
    document.addEventListener("keydown", handleInteraction);
    return () => {
      document.removeEventListener("mousedown", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
    };
  }, [showFields, isOpen]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatLog]);

  const sendHint = async () => {
    if (!currentHint.trim()) return;
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE}/app/API/rbmgt/chat/suggestReply.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookingId, hint: currentHint }),
        }
      );

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (err) {
        console.error("JSON parse error:", err);
        setChatLog((prev) => [
          ...prev,
          { role: "user", content: currentHint },
          { role: "assistant", content: "⚠️ Invalid server response." },
        ]);
        setCurrentHint("");
        setLoading(false);
        return;
      }

      const decodedReply = data.success
        ? decodeURIComponent(data.reply.replace(/\+/g, " "))
        : "⚠️ GPT failed to reply.";

      setChatLog((prev) => [
        ...prev,
        { role: "user", content: currentHint },
        { role: "assistant", content: decodedReply },
      ]);
    } catch (err) {
      console.error("Network error:", err);
      setChatLog((prev) => [
        ...prev,
        { role: "user", content: currentHint },
        { role: "assistant", content: "⚠️ Network error. Try again later." },
      ]);
    } finally {
      setCurrentHint("");
      setLoading(false);
    }
  };

  const handleInsert = (value) => {
    console.log("📌 Inserted value:", value);
    setCurrentHint((prev) => prev + value);
    setShowFields(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const lastReply = [...chatLog].reverse().find((m) => m.role === "assistant")?.content;

  useEffect(() => {
    console.log("🧾 booking object received:", booking);
    console.log("🔑 sortedKeys:", Object.keys(booking).sort());
  }, [booking]);

  const handleTextareaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendHint();
    }
  };

  return (
    <div className="suggested-reply-float">
      <button className="toggle-btn" onClick={() => setIsOpen(!isOpen)}>💡</button>

      {isOpen && (
        <div className="suggested-reply-box">
          <button
            className="clear-log-icon"
            title="Clear chat log"
            onClick={() => {
              if (window.confirm("Are you sure you want to clear the chat log?")) {
                setChatLog([]);
              }
            }}
          >
            🗑
          </button>

          <div className="chat-history">
            {chatLog.map((msg, idx) => (
              <div key={idx} className={`chat-bubble ${msg.role}`}>
                {msg.content}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <div className="textarea-wrapper">
            <textarea
              ref={textareaRef}
              value={currentHint}
              onChange={(e) => setCurrentHint(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
              placeholder="Type a hint or follow-up message..."
              rows={2}
              style={{ maxHeight: "150px", overflowY: "auto" }}
            />
          </div>

          <div className="chat-controls">
            <InsertFieldButton
              showFields={showFields}
              toggleFields={setShowFields}
              booking={booking}
              onInsert={handleInsert}
            />

            <button onClick={sendHint} disabled={loading}>
              {loading ? "Thinking..." : "Send Hint"}
            </button>
            <button
              disabled={!lastReply}
              onClick={() => {
                onInject?.(lastReply);
                setIsOpen(false);
              }}
              className="use-reply-button"
            >
              ✅ Use this reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
